import { styled } from '@mui/system'
import { V, H, VItem } from 'components/layout'
import { toDateString } from 'common/misc'
import { unitConvert } from '@farmtrx/units'
import { isNil } from 'es-toolkit/compat'
import { t } from 'i18next'

const HECTARES_TO_ACRES = 2.471054
const TONNES_TO_POUNDS = 2204.622622
const COORDINATE_DECIMALS = 6
const TEMPERATURE_DECIMALS = 1

function getYieldToPref(data, prefsUnit, cropBuPerTonne) {
  // GRAY_INDEX is the raster yield in g/m2 that is returned by the WFS query
  /* {
      "type": "FeatureCollection",
      "features": [
          {
              "type": "Feature",
              "id": "",
              "geometry": null,
              "properties": {
                  "GRAY_INDEX": 433.30535888671875
              }
          }
      ],
      "totalFeatures": "unknown",
      "numberReturned": 1,
      "timeStamp": "2025-08-10T13:20:20.359Z",
      "crs": null
    }
  */
  const yieldKeys = {
    tonnes_ha: ['yldt_ha', 'YLDt_ha', 'GRAY_INDEX'],
    bu_ac: ['yldbu_ac', 'YLDbu_ac', 'GRAY_INDEX'],
    kg_ha: ['yldkg_ha', 'YLDkg_ha', 'GRAY_INDEX'],
  }
  let keys = yieldKeys[prefsUnit]
  let value
  if (isNil(keys)) {
    keys = yieldKeys['tonnes_ha']
    for (let i = 0; i < keys.length; i++) {
      if (!isNil(data[keys[i]])) {
        value = data[keys[i]]
      }
    }

    if (isNil(value)) {
      return null
    }
    value = unitConvert.convertYieldToPref(value, prefsUnit, cropBuPerTonne)
  } else {
    for (let i = 0; i < keys.length; i++) {
      if (!isNil(data[keys[i]])) {
        if (keys[i] === 'GRAY_INDEX') {
          // the raster yield is always in g/m2
          value = unitConvert.convertYieldToPref(
            data[keys[i]],
            prefsUnit,
            cropBuPerTonne,
          )
        } else {
          value = data[keys[i]]
        }
      }
    }
  }
  if (isNil(value)) {
    return null
  }
  return `${value.toFixed(2)} ${t(prefsUnit)}`
}

export default function InfoList({ data, scrollable = true }) {
  const harvestTimestamp = useMemo(
    () => (isNil(data.timestamp) ? null : new Date(data.timestamp * 1000)),
    [data],
  )
  const prefs = useSelector((state) => state.account.singleton.prefs)
  const { units } = prefs

  const { t } = useTranslation()

  const yieldValue = getYieldToPref(data, units.yieldunit_in)

  // Keys with capital letters are legacy data which may or may not exist
  const possibleEntries = [
    {
      label: t('crop'),
      keys: ['crop', 'Crop'],
      convert: (crop) => {
        return t(crop.toLowerCase())
      },
    },
    {
      label: t('yield'),
      value: yieldValue,
    },
    {
      label: t('moisture'),
      keys: ['moisture', 'Moisture'],
      convert: (moisture) => {
        return `${moisture.toFixed(1)} %`
      },
    },
    {
      label: t('speed'),
      keys: ['speedkmh', 'SpeedKmh'],
      convert: (speed) => {
        let value = unitConvert.convertSpeed(speed, units.speed_in).toFixed(2)
        return `${value} ${t(units.speed_in)} `
      },
    },
    {
      label: t('harvest_date'),
      value: harvestTimestamp ? toDateString(harvestTimestamp) : null,
    },
    {
      label: t('harvest_time'),
      value: harvestTimestamp
        ? `${harvestTimestamp.toLocaleTimeString([], { hour12: false })} `
        : null,
    },
    {
      label: t('latitude'),
      keys: ['latitude', 'Latitude'],
      convert: (latitude) => Number(latitude).toFixed(COORDINATE_DECIMALS),
    },
    {
      label: t('longitude'),
      keys: ['longitude', 'Longitude'],
      convert: (longitude) => Number(longitude).toFixed(COORDINATE_DECIMALS),
    },
    {
      label: t('combine'),
      keys: ['combine', 'Combine'],
    },
    {
      label: t('header_width'),
      keys: ['headerw_m', 'HeaderW_M'],
      convert: (headerWidthMeters) => {
        if (units.length_in == unitConvert.FEET) {
          const { feet, inches } =
            unitConvert.convertToFeetInches(headerWidthMeters)
          return `${feet}' ${inches}"`
        } else if (units.length_in === unitConvert.INCHES) {
          return `${Math.round(headerWidthMeters * unitConvert.METRES_TO_INCHES)} ${t(
            units.length_in,
          )}`
        }
        return `${headerWidthMeters} ${t(units.length_in)}`
      },
    },
    {
      label: t('header_height'),
      keys: ['header_hi'],
      convert: (headerHeightmm) => {
        if (headerHeightmm < 0) {
          return null
        }
        const headerHeightm = headerHeightmm / unitConvert.METERS_TO_MM
        if (units.length_in === unitConvert.FEET) {
          const { feet, inches } =
            unitConvert.convertToFeetInches(headerHeightm)
          return `${feet}' ${inches}"`
        } else if (units.length_in === unitConvert.INCHES) {
          return `${Math.round(headerHeightm * unitConvert.METRES_TO_INCHES)} ${t(
            units.length_in,
          )}`
        }
        return `${headerHeightm} ${t(units.length_in)}`
      },
    },
    {
      label: t('elevation'),
      keys: ['elevation'],
      convert: (elevationMeters) => {
        const lengthUnit = units.length_in ?? unitConvert.METERS
        const value = unitConvert.convertLength(
          elevationMeters,
          lengthUnit,
          unitConvert.METERS,
        )
        return `${value.toFixed(1)} ${t(lengthUnit)}`
      },
    },
    {
      // Swath boxes are tens of square metres, so the field-scale area unit
      // (ha/ac) would read as 0.0022; follow the length unit instead.
      label: t('area'),
      keys: ['sq_m'],
      convert: (squareMeters) => {
        if (units.length_in === unitConvert.METERS) {
          return `${squareMeters.toFixed(1)} ${t('square_metres_abbr')}`
        }
        const feetPerMeter = unitConvert.convertLength(
          1,
          unitConvert.FEET,
          unitConvert.METERS,
        )
        const squareFeet = squareMeters * feetPerMeter ** 2
        return `${squareFeet.toFixed(0)} ${t('square_feet_abbr')}`
      },
    },
    {
      label: t('test_weight'),
      keys: ['testweight'],
      convert: (testWeightKgHl) => {
        const testWeightUnit = units.testweight_in ?? unitConvert.KG_HL
        const value = unitConvert.convertTestweight(
          testWeightKgHl,
          unitConvert.KG_HL,
          testWeightUnit,
        )
        return `${value.toFixed(1)} ${t(testWeightUnit)}`
      },
    },
    {
      label: t('operator'),
      keys: ['operator'],
    },
    {
      label: t('temperature'),
      keys: ['temperatur', 'Temperatur'],
      convert: (temp) => {
        if (temp === -99) {
          return null
        }
        if (units.temperature_in === 'fahrenheit') {
          temp = (temp * 9) / 5 + 32
        }
        return `${Number(temp).toFixed(TEMPERATURE_DECIMALS)} ${t(units.temperature_in)}`
      },
    },
    {
      label: t('time_delay'),
      keys: ['timedel', 'TimeDel'],
    },
    {
      label: t('calibration'),
      keys: ['calibr', 'Calibr'],
    },
    {
      label: t('ym_id'),
      keys: ['ym_id', 'YM_id'],
    },
  ]

  const labelsValues = useMemo(() => {
    let entries = []
    for (let i = 0; i < possibleEntries.length; i++) {
      const keys = possibleEntries[i].keys
      if (!isNil(possibleEntries[i].value)) {
        let entry = {
          label: possibleEntries[i].label,
          value: possibleEntries[i].value,
        }
        entries.push(entry)
      } else if (!isNil(keys)) {
        for (let j = 0; j < keys.length; j++) {
          if (!isNil(data[keys[j]])) {
            const convert = possibleEntries[i]?.convert
            let entry = {
              label: possibleEntries[i].label,
              value: !isNil(convert) ? convert(data[keys[j]]) : data[keys[j]],
            }
            if (!isNil(entry.value)) {
              entries.push(entry)
            }
          }
        }
      }
    }
    return entries
  }, [data, harvestTimestamp])

  return (
    <PointDataList $scrollable={scrollable}>
      {labelsValues.map((entry, idx) => {
        return (
          <Line key={idx} $space>
            <H $justifyContent="space-between">
              <H.Item>
                <label>{entry.label}</label>
              </H.Item>
              <H.Item>
                <InfoData title={entry.value}>{entry.value}</InfoData>
              </H.Item>
            </H>
          </Line>
        )
      })}
    </PointDataList>
  )
}

// The hover popup can't be scrolled (it ignores pointer events), so it shows
// the whole list instead.
const PointDataList = styled(V, {
  shouldForwardProp: (prop) => prop !== '$scrollable',
})(({ $scrollable }) => ({
  fontSize: '0.625rem',
  lineHeight: 1.15,
  '& label': { margin: 0, fontSize: 'inherit' },
  ...($scrollable
    ? { maxHeight: '220px', overflowY: 'scroll', paddingRight: '8px' }
    : {}),
}))

const InfoData = styled('span')({
  float: 'right',
  width: '150px',
  fontWeight: 600,
  overflow: 'hidden',
  textAlign: 'right',
  textOverflow: 'ellipsis',
})

const Line = styled(VItem)({
  width: '250px',
  padding: 0,
})
